import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import ProductModal from './ProductModal';
import shoe3 from './images/shoe3.png';
import shoe7 from './images/shoe7.png';
import shoe8 from './images/shoe8.png';
import shoe9 from './images/shoe9.png';
import sh1 from './images/sh1.png';
import sh2 from './images/sh2.png';

gsap.registerPlugin(ScrollTrigger);

const NewArrivals = () => {
  const sectionRef = useRef();
  const carouselRef = useRef();
  const animationRef = useRef();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productsInCart, setProductsInCart] = useState(new Set());
  const navigate = useNavigate();
  const { cartItems } = useCart();

  const newArrivals = [
    { 
      id: 1, 
      name: "Air Max 97", 
      category: "Lifestyle",
      price: 170, 
      image: shoe3, 
      isNew: true,
      description: "The Nike Air Max 97 brings back the iconic design with full-length Max Air cushioning and a sleek, futuristic silhouette.",
      badge: "NEW",
      colors: ["Silver", "Black", "White"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.6,
      reviews: 892
    },
    { 
      id: 2, 
      name: "Blazer Mid", 
      category: "Classic",
      price: 100, 
      image: shoe7, 
      isNew: true,
      description: "The Nike Blazer Mid brings a timeless basketball look with premium materials and vintage-inspired details.",
      badge: "NEW",
      colors: ["White", "Black", "Navy"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.4,
      reviews: 567
    },
    { 
      id: 3, 
      name: "Dunk Low", 
      category: "Street",
      price: 110, 
      image: shoe8, 
      isNew: false,
      description: "The Nike Dunk Low returns with crisp overlays and original team colors for a classic basketball-inspired look.",
      badge: "POPULAR",
      colors: ["White/Black", "University Blue", "Grey"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.7,
      reviews: 1234
    },
    { 
      id: 4, 
      name: "Air Jordan 1", 
      category: "Basketball",
      price: 170, 
      image: shoe9, 
      isNew: true,
      description: "The Air Jordan 1 retains its appeal with classic design elements and premium materials for modern comfort.",
      badge: "NEW",
      colors: ["Bred", "Royal", "Chicago"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.8,
      reviews: 2156
    },
    { 
      id: 5, 
      name: "Air Max 90", 
      category: "Running",
      price: 120, 
      image: sh1, 
      isNew: false,
      description: "The Nike Air Max 90 stays true to its OG running roots with iconic Waffle outsole and visible Max Air cushioning.",
      badge: "CLASSIC",
      colors: ["White", "Black", "Infrared"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.5,
      reviews: 1876
    },
    { 
      id: 6, 
      name: "React Element", 
      category: "Running",
      price: 130, 
      image: sh2, 
      isNew: true,
      description: "Nike React Element combines innovative React foam with bold design elements for responsive comfort and style.",
      badge: "NEW",
      colors: ["Neptune Green", "Black", "Undercover"],
      sizes: [7, 8, 9, 10, 11, 12],
      rating: 4.3,
      reviews: 445
    }
  ];

  const openModal = (product, focusOnSize = false) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    // Pause auto-scroll when modal opens
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // If focusOnSize is true, we'll pass this info to ProductModal
    if (focusOnSize) {
      setSelectedProduct({ ...product, focusOnSize: true });
    }
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setIsModalOpen(false);
    // Resume auto-scroll when modal closes
    const scroll = () => {
      if (carouselRef.current) {
        carouselRef.current.scrollLeft += 1;
        if (carouselRef.current.scrollLeft >= carouselRef.current.scrollWidth / 2) {
          carouselRef.current.scrollLeft = 0;
        }
      }
      animationRef.current = requestAnimationFrame(scroll);
    };
    animationRef.current = requestAnimationFrame(scroll);
  };

  const handleAddToCart = (cartItem) => {
    console.log('Added to cart:', cartItem);
    // Here you would typically add to cart state or send to backend
    alert(`Added ${cartItem.product.name} (Size: ${cartItem.size}, Color: ${cartItem.color}, Qty: ${cartItem.quantity}) to cart!`);
  };

  const handleCartAction = (product) => {
    if (productsInCart.has(product.id)) {
      // Product is in cart, go to cart page
      navigate('/cart');
    } else {
      // Product not in cart, open modal for adding
      openModal(product, true);
    }
  };

  // Track products in cart
  useEffect(() => {
    const productIds = new Set(cartItems.map(item => item.id));
    setProductsInCart(productIds);
  }, [cartItems]);

  useEffect(() => {
    // Animate section title
    gsap.fromTo(sectionRef.current.querySelector('h2'),
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Auto-scroll carousel
    const startScrolling = () => {
      const scroll = () => {
        if (carouselRef.current) {
          carouselRef.current.scrollLeft += 1;
          if (carouselRef.current.scrollLeft >= carouselRef.current.scrollWidth / 2) {
            carouselRef.current.scrollLeft = 0;
          }
        }
        animationRef.current = requestAnimationFrame(scroll);
      };
      animationRef.current = requestAnimationFrame(scroll);
    };

    startScrolling();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const duplicatedArrivals = [...newArrivals, ...newArrivals];

  return (
    <Section ref={sectionRef}>
      <Container>
        <SectionHeader>
          <SectionTitle>New Arrivals</SectionTitle>
          <SectionSubtitle>Fresh drops, endless possibilities</SectionSubtitle>
        </SectionHeader>
        
        <CarouselContainer
          ref={carouselRef}
          onMouseEnter={() => animationRef.current && cancelAnimationFrame(animationRef.current)}
          onMouseLeave={() => {
            const scroll = () => {
              if (carouselRef.current) {
                carouselRef.current.scrollLeft += 1;
                if (carouselRef.current.scrollLeft >= carouselRef.current.scrollWidth / 2) {
                  carouselRef.current.scrollLeft = 0;
                }
              }
              animationRef.current = requestAnimationFrame(scroll);
            };
            animationRef.current = requestAnimationFrame(scroll);
          }}
        >
          {duplicatedArrivals.map((product, index) => (
            <ProductCard 
              key={`${product.id}-${index}`}
              onClick={() => openModal(product)}
            >
              {product.isNew && <NewBadge>NEW</NewBadge>}
              <ProductImage>
                <img src={product.image} alt={product.name} />
              </ProductImage>
              <ProductInfo>
                <ProductName>{product.name}</ProductName>
                <ProductPrice>${product.price}</ProductPrice>
                <ProductActions>
                  <AddToCartButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCartAction(product);
                    }}
                    isInCart={productsInCart.has(product.id)}
                  >
                    {productsInCart.has(product.id) ? (
                      <>
                        <CartIconSmall>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="9" cy="21" r="1"></circle>
                            <circle cx="20" cy="21" r="1"></circle>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                          </svg>
                        </CartIconSmall>
                        Go to Cart
                      </>
                    ) : (
                      'Add to Cart'
                    )}
                  </AddToCartButton>
                </ProductActions>
              </ProductInfo>
            </ProductCard>
          ))}
        </CarouselContainer>
      </Container>
      
      {/* Product Modal */}
      <ProductModal
        isOpen={isModalOpen}
        product={selectedProduct}
        onClose={closeModal}
        onAddToCart={handleAddToCart}
      />
    </Section>
  );
};

export default NewArrivals;

const Section = styled.section`
  padding: 100px 0;
  background: #f8f8f8;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-family: 'Anton', sans-serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: #111;
  margin-bottom: 16px;
  letter-spacing: 2px;
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: #666;
  font-style: italic;
`;

const CarouselContainer = styled.div`
  display: flex;
  gap: 30px;
  overflow-x: hidden;
  padding: 20px 0;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ProductCard = styled.div`
  position: relative;
  flex: 0 0 280px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  }
`;

const NewBadge = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  background: #ff4757;
  color: white;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 20px;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ProductImage = styled.div`
  height: 200px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 80%;
    height: 80%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  ${ProductCard}:hover & img {
    transform: scale(1.1);
  }
`;

const ProductInfo = styled.div`
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ProductName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin-bottom: 8px;
`;

const ProductPrice = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111;
  margin-bottom: 12px;
`;

const ProductActions = styled.div`
  margin-top: auto;
`;

const AddToCartButton = styled.button`
  width: 100%;
  background: ${props => props.isInCart ? '#2980b9' : '#111'};
  color: white;
  border: none;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  font-size: 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:hover {
    background: ${props => props.isInCart ? '#1f5582' : '#000'};
    transform: translateY(-1px);
  }
`;

const CartIconSmall = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    transition: transform 0.3s ease;
  }
`;

